// components/molecules/react-async-multiselect-field.jsx

import React, { useState } from 'react'
import { Badge, FormGroup, Label, FormText } from 'reactstrap'
import { useField } from 'formik'
import AsyncSelect from 'react-select/async'
import PropTypes from 'prop-types'

const propTypes = {
  id: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string
  ]),
  name               : PropTypes.string,
  label              : PropTypes.string,
  placeholder        : PropTypes.string,
  formGroupClassName : PropTypes.string,
  formText           : PropTypes.string,
  required           : PropTypes.bool,
  isLoading          : PropTypes.bool,
  setInputValue      : PropTypes.func,
  getOptionLabel     : PropTypes.func,
  loadOptions        : PropTypes.func,
  handleCustomChange : PropTypes.func,
  dataSelected       : PropTypes.array
}

/**
 * Multiselect Async Input field that build with `react-select/async`.
 * Only to be used within a Formik component.
 *
 * @param {string} label - Field label
 * @param {string} placeholder - Field placeholder
 * @param {booelan} required - Display a Required badge next to label
 * @param {bool} isLoading - Loading when searching the option
 * @param {func} formText - Description field
 * @param {func} formGroupClassName - Extended class name for `<FormGroup>`
 * @param {func} setInputValue - function to set input value to state
 * @param {func} getOptionLabel - function that returns label to display the option object
 * @param {func} loadOptions - async function called when input changed
 * @param {object} props - handles `name` prop.
 * @param {func} handleCustomChange - customize handle change for specific situation
 * @param {string} dataSelected - selected data from handle custom change func
 */

const ReactAsyncMultiselectField = ({
  label,
  placeholder,
  required,
  isLoading,
  formText,
  formGroupClassName,
  setInputValue,
  getOptionLabel,
  loadOptions,
  handleCustomChange,
  dataSelected,

  /* ✅ NEW */
  formatOptionLabel,
  selectStyles,

  ...props
}) => {
  const [field, meta, helper] = useField(props)
  const invalid = meta.touched && !!meta.error

  const [selectedOptions, setSelectedOptions] = useState([])
  const value = dataSelected ?? selectedOptions

  const handleInputChange = (value) => {
    setInputValue?.(value)
    return value
  }

  const defaultHandleChange = (options) => {
    if (options) {
      helper.setValue(options.map(o => o.id))
    } else {
      helper.setValue([])
    }
    setSelectedOptions(options || [])
  }

  const onChange = handleCustomChange || defaultHandleChange

  return (
    <FormGroup className={formGroupClassName}>
      {label && (
        <Label className="text-gray-light">
          <strong>{label}</strong>
          {required && <Badge color="warning" className="ml-1">Required</Badge>}
        </Label>
      )}

      <AsyncSelect
        {...field}
        {...props}
        cacheOptions
        isMulti
        isLoading={isLoading}
        value={value}
        loadOptions={loadOptions}
        onInputChange={handleInputChange}
        onChange={onChange}
        getOptionLabel={getOptionLabel}
        getOptionValue={(o) => String(o.id)}

        /* ✅ THIS FIXES EVERYTHING */
        formatOptionLabel={formatOptionLabel}
        styles={selectStyles}

        placeholder={placeholder}
      />

      {formText && <FormText>{formText}</FormText>}
      {invalid && <div style={{ color: 'red', marginTop: 6 }}>{meta.error}</div>}
    </FormGroup>
  )
}

ReactAsyncMultiselectField.propTypes = {
  name: PropTypes.string.isRequired,
  loadOptions: PropTypes.func.isRequired,
  getOptionLabel: PropTypes.func,
  formatOptionLabel: PropTypes.func,
  selectStyles: PropTypes.object,
}

export default ReactAsyncMultiselectField
